import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

import { Button } from 'components/Button';

import { ReactComponent as CalendarIcon } from './assets/calendar.svg';

import {
  StyledInputWrapper,
  StyledDatePicker,
  StyledDropdownContent,
  StyledInput,
  StyledDropdownFooter,
} from './styled';
import CleanInputIcon from 'components/Form/shared/CleanInputIcon';
import Conditional from 'components/Conditional';
import { StyledError } from '../FormGroup/styled';

const initialState = {
  date: new Date(),
  formattedISO: '',
};

const DatePicker = props => {
  const {
    size,
    value,
    placeholder,
    className = '',
    onChange,
    error,
    errormessage,
    disabled,
    'data-cy': dataCy,
    width = 'auto',
  } = props;
  const ref = React.createRef();
  const [formattedValue, setFormattedValue] = useState(() => {
    if (!value) return '';
    try {
      return format(new Date(value), 'dd/MM/yyyy');
    } catch {
      return '';
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState(() => {
    if (!value) return initialState.date;
    try {
      return new Date(value);
    } catch {
      return initialState.date;
    }
  });

  // Track last value processed so onChange is not called on mount spuriously
  const lastValueRef = useRef(value);

  // const [toastList, setToastList] = useState([]);

  useEffect(() => {
    if (lastValueRef.current === value) return;
    lastValueRef.current = value;

    try {
      if (!value) {
        setDate(new Date());
        setFormattedValue('');
        onChange && onChange('');
      } else {
        handleChange(value);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error({ error });
    }
    // eslint-disable-next-line
  }, [value]);

  const toggle = () => setIsOpen(!isOpen);

  const handleChange = date => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const formattedValue = format(dateObj, 'dd/MM/yyyy');

    setFormattedValue(formattedValue);
    setDate(dateObj);
    onChange && onChange(date);
  };

  const handleClearDate = () => {
    setDate(initialState.date);
    setFormattedValue('');

    onChange && onChange('');
  };
  const handleClickOutside = useCallback(
    e => {
      if (ref?.current && ref.current.contains(e.target)) {
        // inside click
        return;
      }

      // outside click
      // onChange(e.target.value)
      setIsOpen(false);
    },
    [ref],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClickOutside, ref]);

  return (
    <StyledInputWrapper className={className} ref={ref} width={width}>
      <CalendarIcon width={13} height={13} style={{ flexShrink: 0 }} />
      <StyledInput
        readOnly
        error={error}
        type="text"
        data-cy={dataCy}
        size={size}
        placeholder={placeholder}
        value={formattedValue || ''}
        onClick={toggle}
        disabled={disabled}
      />
      {/* <Toast toastList={toastList} autoDelete dismissTime={7000} /> */}
      <Conditional when={formattedValue !== ''}>
        <CleanInputIcon size={size} onClick={handleClearDate} />
      </Conditional>
      <StyledDropdownContent isOpen={isOpen}>
        <StyledDatePicker
          locale={pt}
          onChange={handleChange}
          date={date}
          // editableDateInputs
          dateDisplayFormat="dd/MM/yyyy"
        />
        <StyledDropdownFooter>
          <Button type="button" onClick={() => setIsOpen(false)}>
            Aplicar
          </Button>
        </StyledDropdownFooter>
      </StyledDropdownContent>
      <StyledError>
        <p>{error && errormessage}</p>
      </StyledError>
    </StyledInputWrapper>
  );
};

DatePicker.prototype = {
  size: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
};

export { DatePicker };
